/**
 * API URL Override Utility
 *
 * Allows runtime override of API URLs via query parameters for PR testing.
 * Only works in development/testing environments.
 *
 * Usage:
 *   - ?hw_api_pr=123 → Override hunkware-api to PR #123
 *   - ?php_api_pr=123 → Override php-api to PR #123
 *   - ?clear_overrides → Clear all overrides
 *
 * Overrides persist in sessionStorage for the browser session.
 */

export type ApiType = 'hw_api' | 'php_api';

const STORAGE_KEYS: Record<ApiType, string> = {
  hw_api: 'pr_override_hw_api',
  php_api: 'pr_override_php_api',
};

const PR_URL_PATTERNS: Record<ApiType, (pr: number) => string> = {
  hw_api: pr => `https://hunkware-api-pr-${pr}.dev.apps.private.chhj.com/api`,
  php_api: pr => `https://api-dev-php-pr-${pr}.php.apps.private.chhj.com/api/v1`,
};

/**
 * Check if the current environment allows API overrides.
 * Only localhost, testing, and dev environments are allowed.
 */
export const isOverrideAllowed = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.includes('.testing.') || hostname.includes('.dev.');
};

/**
 * Initialize API overrides from URL query parameters.
 * Should be called early in the app lifecycle, before API clients are initialized.
 */
export const initializeApiOverrides = (): void => {
  if (!isOverrideAllowed()) return;

  const params = new URLSearchParams(window.location.search);

  // Clear all overrides if requested
  if (params.has('clear_overrides')) {
    clearAllOverrides();
    return;
  }

  // Set overrides from query params
  (Object.keys(STORAGE_KEYS) as ApiType[]).forEach(apiType => {
    const paramKey = `${apiType}_pr`;
    const prParam = params.get(paramKey);
    if (prParam) {
      const prNumber = parseInt(prParam, 10);
      if (prNumber > 0) {
        sessionStorage.setItem(STORAGE_KEYS[apiType], prNumber.toString());
      }
    }
  });
};

/**
 * Get the API URL, applying any active override.
 * Checks both sessionStorage and URL query params (in case initializeApiOverrides
 * hasn't been called yet).
 *
 * @param apiType - The type of API ('hw_api' or 'php_api')
 * @param defaultUrl - The default URL to use if no override is active
 * @returns The API URL to use (either default or overridden)
 */
export const getApiUrl = (apiType: ApiType, defaultUrl: string): string => {
  if (!isOverrideAllowed()) return defaultUrl;

  // First check sessionStorage
  const storedPr = sessionStorage.getItem(STORAGE_KEYS[apiType]);
  if (storedPr) {
    const prNumber = parseInt(storedPr, 10);
    if (prNumber > 0) {
      return PR_URL_PATTERNS[apiType](prNumber);
    }
  }

  // Also check query params directly (for first page load before initializeApiOverrides runs)
  const params = new URLSearchParams(window.location.search);
  const paramKey = `${apiType}_pr`;
  const prParam = params.get(paramKey);
  if (prParam) {
    const prNumber = parseInt(prParam, 10);
    if (prNumber > 0) {
      // Also store it for subsequent calls
      sessionStorage.setItem(STORAGE_KEYS[apiType], prNumber.toString());
      return PR_URL_PATTERNS[apiType](prNumber);
    }
  }

  return defaultUrl;
};

/**
 * Get all active override states.
 */
export const getActiveOverrides = (): Record<ApiType, number | null> => ({
  hw_api: getPrOverride('hw_api'),
  php_api: getPrOverride('php_api'),
});

/**
 * Get the PR number for a specific API type override.
 */
export const getPrOverride = (apiType: ApiType): number | null => {
  if (!isOverrideAllowed()) return null;
  const stored = sessionStorage.getItem(STORAGE_KEYS[apiType]);
  return stored ? parseInt(stored, 10) : null;
};

/**
 * Check if any overrides are currently active.
 */
export const hasActiveOverrides = (): boolean => {
  if (!isOverrideAllowed()) return false;
  return Object.values(STORAGE_KEYS).some(key => sessionStorage.getItem(key) !== null);
};

/**
 * Clear a specific API override.
 */
export const clearOverride = (apiType: ApiType): void => {
  sessionStorage.removeItem(STORAGE_KEYS[apiType]);
};

/**
 * Clear all API overrides.
 */
export const clearAllOverrides = (): void => {
  Object.values(STORAGE_KEYS).forEach(key => sessionStorage.removeItem(key));
};

/**
 * Get a human-readable label for an API type.
 */
export const getApiLabel = (apiType: ApiType): string => {
  const labels: Record<ApiType, string> = {
    hw_api: 'Hunkware API',
    php_api: 'PHP API',
  };
  return labels[apiType];
};
